<template>
  <div class="pa-page" :class="{ 'pa-page--customer': channel === 'customer' }">
      <header class="pa-header">
        <button type="button" class="back-btn" @click="$router.push({ name: productsRoute })">
          <v-icon size="18" color="#1E4276">mdi-arrow-left</v-icon>
        </button>
        <div>
          <p class="breadcrumb">{{ pageBreadcrumb }}</p>
          <h1 class="title">{{ pageTitle }}</h1>
        </div>
        <span v-if="mockEnabled" class="demo-badge">Demo mode</span>
      </header>

      <customer-agent-picker v-if="channel === 'customer' && !guest" />

      <div v-if="loading" class="loading-wrap">
        <v-progress-circular indeterminate color="#00AEEF" />
        <span>Loading rate card…</span>
      </div>

      <div v-else class="quote-container">
        <div class="quote-layout">
          <div class="quote-form-column">
            <div class="section-card">
              <div class="section-header">
                <span class="section-number">1</span>
                <h3>Scope of coverage</h3>
              </div>
              <pa-course-duration-selector v-model="courseDuration" />
              <pa-rate-card-table
                class="tw-mt-4"
                :options="rateCard.options"
                :premiums="rateCard.premiums"
                :selected-option="optionCode"
                :course-duration="courseDuration"
                @select-option="onOptionChange"
              />
            </div>
          </div>

          <aside class="quote-sidebar">
            <pa-quote-sidebar
              :option-name="selectedOptionName"
              :duration-label="selectedDurationLabel"
              :premium="premium"
              :generating="submitting"
              :can-continue="canContinue"
              :quote="activeQuote"
              :mock-enabled="mockEnabled"
              :continue-label="continueSidebarLabel"
              @continue="goToProposal"
            />
          </aside>
        </div>
      </div>
  </div>
</template>

<script>
import PaCourseDurationSelector from '@/components/agent/pa/PaCourseDurationSelector';
import PaRateCardTable from '@/components/agent/pa/PaRateCardTable';
import PaQuoteSidebar from '@/components/agent/pa/PaQuoteSidebar';
import CustomerAgentPicker from '@/components/shared/CustomerAgentPicker';
import { getPaPremiumFromRateCard } from '@/constants/paStudentsRateCard';
import {
  getPaRateCard, createPaQuotation, updatePaQuotation, recalculatePaPremium, getPaQuotation, isAgentPaMockEnabled,
  resolveAgentShortCode,
} from '@/services/agentPaApi';
import { savePaPendingQuote, loadPaPendingQuote } from '@/utils/saleCache';
import { buildPaCreatePayload, getPaChannelConfig, referralPaAgentId } from '@/utils/paChannel';
import {
  GUEST_SESSION_KEY,
  GUEST_QUOTE_ROUTES,
  guestBuyRouteForProduct,
  redirectGuestToBuy,
} from '@/utils/guestQuote';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'PaPlanSelectPage',
  components: {
    PaCourseDurationSelector,
    PaRateCardTable,
    PaQuoteSidebar,
    CustomerAgentPicker,
  },
  props: {
    channel: {
      type: String,
      required: true,
      validator: (v) => v === 'agent' || v === 'customer',
    },
    guest: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: true,
      submitting: false,
      quoteTimer: null,
      rateCard: { options: [], premiums: [] },
      optionCode: 'OPTION_2',
      courseDuration: '1_YEAR',
      activeQuote: {},
      agnShtDesc: 'DIRECT',
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser']),
    ...mapGetters('referral', ['referralAgent']),
    referralPaAgentId() {
      if (this.channel !== 'customer') return null;
      return referralPaAgentId(this.referralAgent);
    },
    paConfig() {
      return getPaChannelConfig({ paChannel: this.channel });
    },
    productsRoute() {
      return this.guest ? GUEST_QUOTE_ROUTES.products : this.paConfig.routes.products;
    },
    pageBreadcrumb() {
      return this.guest ? 'Get quote / Personal Accident' : this.paConfig.labels.planBreadcrumb;
    },
    pageTitle() {
      return this.guest ? 'Students PA — Get a quote' : this.paConfig.labels.planTitle;
    },
    sessionEmail() {
      return this.authenticatedUser?.email || (this.guest ? GUEST_SESSION_KEY : '');
    },
    mockEnabled() {
      return isAgentPaMockEnabled();
    },
    continueSidebarLabel() {
      if (this.guest) return 'Proceed to buy';
      if (this.paConfig.labels.continueSidebar) {
        return this.paConfig.labels.continueSidebar;
      }
      return this.journey === 'direct' ? 'Convert to sale' : 'Continue to complete';
    },
    premium() {
      if (this.activeQuote?.premium != null) {
        return Number(this.activeQuote.premium);
      }
      return getPaPremiumFromRateCard(this.rateCard, this.courseDuration, this.optionCode);
    },
    canContinue() {
      return this.premium > 0 && !!this.activeQuote?.quoteId && !this.submitting;
    },
    selectedOptionName() {
      const o = this.rateCard.options.find((x) => x.code === this.optionCode);
      return o?.name || this.optionCode;
    },
    selectedDurationLabel() {
      const d = this.rateCard.premiums.find((p) => p.courseDuration === this.courseDuration);
      return d?.label || this.courseDuration;
    },
    journey() {
      if (this.paConfig.channel === 'customer') return this.paConfig.defaultJourney;
      return this.$route.query.journey || 'quote';
    },
  },
  async mounted() {
    if (this.paConfig.trackAnalytics) {
      trackAgentInteraction(this, 'pa_plan_select_view', { journey: this.journey });
    }
    if (this.channel === 'customer') {
      await this.$store.dispatch('referral/ensureReferralValidated', this.$route.query);
      this.agnShtDesc = this.guest ? 'DIRECT' : (this.referralPaAgentId || 'DIRECT');
    } else {
      this.agnShtDesc = await resolveAgentShortCode(this.authenticatedUser);
    }
    try {
      const res = await getPaRateCard();
      if (res.success) this.rateCard = res.data;
    } finally {
      this.loading = false;
    }
    const email = this.sessionEmail;
    await this.loadQuoteFromRouteOrSession(email);
    this.$nextTick(() => this.debounceEnsureQuote());
  },
  beforeDestroy() {
    if (this.quoteTimer) clearTimeout(this.quoteTimer);
  },
  methods: {
    async applyPendingQuote(pending, email) {
      if (!pending?.quoteId) return;
      this.optionCode = pending.optionCode || this.optionCode;
      this.courseDuration = pending.courseDuration || this.courseDuration;
      this.activeQuote = pending;
      if (!this.mockEnabled && pending.optionCode && pending.courseDuration) {
        const recalc = await recalculatePaPremium(
          pending.optionCode,
          pending.courseDuration,
          pending.quoteId,
        );
        if (recalc.success && recalc.data) {
          this.activeQuote = { ...pending, ...recalc.data, premium: recalc.data.premium };
          savePaPendingQuote(email, this.activeQuote);
        }
      }
    },
    async loadQuoteFromRouteOrSession(email) {
      const routeQuoteId = this.$route.query.quoteId;
      const liveRouteId = !this.mockEnabled ? /^\d+$/.test(String(routeQuoteId || '')) : !!routeQuoteId;
      if (routeQuoteId && liveRouteId) {
        const res = await getPaQuotation(routeQuoteId, this.channel);
        if (res.success && res.data) {
          await this.applyPendingQuote(res.data, email);
          return;
        }
      }
      const pending = email && loadPaPendingQuote(email, { liveOnly: !this.mockEnabled });
      await this.applyPendingQuote(pending, email);
    },
    onOptionChange(code) {
      this.optionCode = code;
      this.debounceEnsureQuote();
    },
    debounceEnsureQuote() {
      if (this.quoteTimer) clearTimeout(this.quoteTimer);
      this.quoteTimer = setTimeout(() => this.ensureQuote(), 400);
    },
    async refreshQuotePremium() {
      const email = this.sessionEmail;
      const payload = {
        optionCode: this.optionCode,
        courseDuration: this.courseDuration,
        premium: this.premium,
      };
      const res = await updatePaQuotation(this.activeQuote.quoteId, {
        ...payload,
        quoteId: this.activeQuote.quoteId,
      });
      if (res.success) {
        this.activeQuote = res.data;
        savePaPendingQuote(email, res.data);
      }
    },
    async ensureQuote() {
      if (this.premium <= 0 || this.submitting) return;
      const email = this.sessionEmail;
      if (this.activeQuote?.quoteId) {
        const quoteAgent = String(this.activeQuote.agentId || '').trim();
        const quoteHasAgent = quoteAgent && quoteAgent !== '0';
        if (this.referralPaAgentId && !quoteHasAgent) {
          this.activeQuote = null;
        } else {
          await this.refreshQuotePremium();
          return;
        }
      }
      this.submitting = true;
      try {
        const payload = buildPaCreatePayload(this.paConfig, {
          optionCode: this.optionCode,
          courseDuration: this.courseDuration,
          premium: this.premium,
          journey: this.journey,
          agentId: this.guest ? null : this.referralPaAgentId,
          agnShtDesc: this.agnShtDesc,
        });
        payload.proposerName = this.authenticatedUser?.name || '';
        payload.email = this.authenticatedUser?.email || '';
        const res = await createPaQuotation(payload);
        if (!res.success) {
          this.$toast.error(res.message || 'Could not prepare quote');
          return;
        }
        this.activeQuote = res.data;
        savePaPendingQuote(email, res.data);
      } finally {
        this.submitting = false;
      }
    },
    async goToProposal() {
      if (!this.activeQuote?.quoteId) {
        await this.ensureQuote();
      }
      if (!this.activeQuote?.quoteId) return;
      const query = { quoteId: this.activeQuote.quoteId };
      if (this.paConfig.channel === 'agent') {
        query.journey = this.journey;
      }
      const buyRoute = guestBuyRouteForProduct('pa', query);
      if (this.guest) {
        redirectGuestToBuy(this.$router, buyRoute);
        return;
      }
      this.$router.push({
        name: this.paConfig.routes.sale,
        query,
      });
    },
  },
  watch: {
    courseDuration() {
      this.debounceEnsureQuote();
    },
    premium() {
      this.debounceEnsureQuote();
    },
    referralAgent: {
      deep: true,
      handler(agent) {
        if (this.channel !== 'customer') return;
        this.agnShtDesc = referralPaAgentId(agent) || 'DIRECT';
        this.debounceEnsureQuote();
      },
    },
  },
  metaInfo: { title: 'Students PA — Select Cover' },
};
</script>

<style scoped>
.pa-page {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 28px 32px;
  box-sizing: border-box;
}
.pa-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.back-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid #E2E8F0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
}
.breadcrumb { font-size: 0.75rem; color: #94A3B8; margin: 0; }
.title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1E4276;
  margin: 4px 0 0;
}
.demo-badge {
  margin-left: auto;
  font-size: 0.65rem;
  font-weight: 600;
  color: #64748B;
  background: #F1F5F9;
  border: 1px solid #E2E8F0;
  padding: 4px 10px;
  border-radius: 20px;
}
.loading-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 48px;
  color: #64748B;
}
.quote-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 24px;
  align-items: start;
}
.section-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  padding: 22px 24px;
  border: 1px solid #E8EDF2;
}
.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
}
.section-number {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #00AEEF;
  color: white;
  font-weight: 700;
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}
.section-header h3 {
  margin: 0;
  font-size: 1.05rem;
  color: #1E4276;
  font-weight: 700;
}
.quote-sidebar { position: sticky; top: 20px; }
@media (max-width: 960px) {
  .pa-page { padding: 16px 16px 28px; }
  .quote-layout { grid-template-columns: 1fr; gap: 16px; }
  .quote-sidebar { position: static; }
  .section-card { padding: 16px; }
}
@media (max-width: 600px) {
  .pa-page { padding: 12px 12px 88px; }
  .title { font-size: 1.15rem; line-height: 1.3; }
  .breadcrumb { font-size: 0.7rem; }
  .pa-header { margin-bottom: 16px; gap: 10px; }
  .section-header h3 { font-size: 0.95rem; }
}
</style>
